/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';

import {AuthenticationService} from './services/authentication.service';

import {PagePermissionType} from "./models/page-permission-type";
import {PagePermissionService} from "./services/page-permission.service";

@Injectable()
export class CanActivateAuthGuard implements CanActivate {
  public static popupActive = false;

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private pagePermissionService: PagePermissionService
  ) {}


  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    if (CanActivateAuthGuard.popupActive) {
      history.pushState(null, null, location.href);
      return false;
    }

    //Retrieve any data required for checking whether to allow the User to navigate to the Page or not
    const pagePermissionType = route.data["pagePermissionType"];

    //Check to see if the User is logged into the system, if not then redirect to the login page
    if (!this.authService.isLoggedIn()) {
      console.warn("CanActivateAuthGuard.canActive() - User not logged in to see Page: " + state.url + "... Redirecting to Login...");
      this.router.navigate(['/login'], {queryParams: {returnUrl: state.url}});
      return false;
    }

    /*
     * At this point:
     *   1) User is logged in
     *
     * See if the User has access to the page, if not return false to indicate this and prevent navigation to the page
     */
    let pageAccess = true;
    {
      if (pagePermissionType != null) {
        pageAccess = this.pagePermissionService.canRead(pagePermissionType)
      }
    }
    if (!pageAccess) {
      console.warn("CanActivateAuthGuard.canActive() - No Permission to view Page: " + state.url);
      this.router.navigate(['/home']);
      return false;
    }

    /*
     * At this point:
     *   1) User is logged in
     *   2) User has access to the page
     *
     * See if the User's Authentication Token is still valid, if not redirect to login
     */
    const validToken = await this.authService.validateToken().then(() => { return true; }, () => { return false; }).catch(() => { return false; });
    if (!validToken) {
        console.warn("CanActivateAuthGuard.canActive() - Invalid Token... Redirecting to login.");
        this.authService.logout();
        this.router.navigate(['/login'], {queryParams: {returnUrl: state.url}});
        return false;
    }

    /*
     * At this point:
     *   1) User is logged in
     *   2) User has access to the page
     *   3) User's Authentication Token is still valid
     *
     * See if the current Licensing is valid, if not redirect to the "Licensing" page.
     * This check should only be done if the User is not already attempting to navigate to the Licensing Page
     */
    if (pagePermissionType === null || pagePermissionType != PagePermissionType.LicensingPage) {
      const validLicense = this.authService.isCurrentLicenseInfoValid();
      if (!validLicense) {
          console.warn("CanActivateAuthGuard.canActive() - License is not valid... Redirecting to Licensing!");
          this.router.navigate(['/licensing']);
          return false;
      }
    }

    /*
     * At this point:
     *   1) User is logged in
     *   2) User has access to the page
     *   3) User's Authentication Token is still valid
     *   4) Current Licensing is valid
     *
     * Allow the User to navigate to the requested Page
     */
    console.log("CanActivateAuthGuard.canActive() - Page => " + state.url);
    return true;
  }

}
